"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { ParsedWebContent } from "@/lib/types/website-content";
import useGet from "@/hooks/useGet";
import { Quiz } from "@/lib/types/quiz";
import QuizSolver from "./quiz-solver";
import { QuizSkeleton } from "./skeletons/home-skeletons";
import Link from "next/link";

interface CareerQuizProps {
  content: ParsedWebContent["quizSection"];
}

export default function CareerQuiz({ content }: CareerQuizProps) {
  const { data: quiz, isLoading } = useGet<Quiz>(`/quiz/random`);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);

  const handleQuizFinish = (result: {
    score: number;
    totalQuestions: number;
    percentage: number;
    isPassing: boolean;
  }) => {
    setQuizCompleted(true);
  };

  const handleStartQuiz = () => {
    setShowQuiz(true);
  };

  const handleTryOtherQuizzes = () => {
    setShowQuiz(false);
    setQuizCompleted(false);
  };

  if (isLoading) {
    return <QuizSkeleton />;
  }

  if (!quiz) {
    return null;
    // <section className="py-12 md:py-16 bg-blue-50 rounded-3xl my-8">
    //   <div className="text-center">
    //     <p className="text-gray-600">No quiz available at the moment.</p>
    //   </div>
    // </section>
    // );
  }

  if (showQuiz) {
    return (
      <section className="py-12 md:py-16 bg-blue-50 rounded-3xl my-8">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            {content?.title || "Find Your Healthcare Career Match"}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {content?.description ||
              "Answer a few questions to discover which healthcare careers might be the best fit for you."}
          </p>
        </div>

        <QuizSolver
          quiz={quiz}
          onFinish={handleQuizFinish}
          showBackButton={false}
          compact={true}
          showRetakeButton={false}
          showTryAnotherButton={false}
        />

        {quizCompleted && (
          <div className="text-center mt-8">
            <Button asChild>
              <Link href="/quizzes">Try Other Quizzes</Link>
            </Button>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="py-12 md:py-16 bg-blue-50 rounded-3xl my-8">
      <div className="text-center mb-12">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
          {content?.title || "Find Your Healthcare Career Match"}
        </h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          {content?.description ||
            "Answer a few questions to discover which healthcare careers might be the best fit for you."}
        </p>
      </div>

      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="text-center">
              Ready to Discover Your Path?
            </CardTitle>
            <CardDescription className="text-center">
              Take a quick quiz to explore healthcare careers that match your
              interests and skills.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <div className="h-24 w-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-green-700 text-4xl">🧠</span>
            </div>
            <p className="text-gray-600 mb-6">
              This quiz will help you understand which healthcare careers align
              with your interests, study preferences, and work environment
              preferences.
            </p>
          </CardContent>
          <CardFooter className="flex justify-center">
            <Button onClick={handleStartQuiz} size="lg">
              Start Quiz
            </Button>
          </CardFooter>
        </Card>
      </div>
    </section>
  );
}
